import { getDefaultMutationName, type ShapeJSON } from '@takeshape/schema';
import { getUserId } from 'app/auth/selectors';
import { getRoutineSaga } from 'app/common/utils/new-sagas';
import { OK, SHOW_ALERT } from 'app/confirm/action-types';
import { getPermission } from 'app/permissions/selectors';
import { getProjectId, getShapeById } from 'app/schema';
import type { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  race,
  select,
  spawn,
  take,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';
import type { ActionType, PayloadMetaAction } from 'typesafe-actions';
import {
  breakLock,
  cleanupEditingSessions,
  contentUnlocked,
  editingSession,
  type GetContentMeta,
  getContent,
  type LockBrokenAction,
  type LockBrokenPayload,
  type LockRoutine,
  lockBroken,
  lockContent,
  unlockContent
} from '../actions';
import { breakLock as breakLockApi, createLock, removeLock } from '../api';
import { getEditingSession, getInactiveLocks } from '../selectors';
import type { ContentId, EditingSession, EditingSessionInfo, Lock } from '../types';

export const breakLockSaga = getRoutineSaga(breakLock, breakLockApi);
export const unlockContentSaga = getRoutineSaga(unlockContent, removeLock);
export const lockContentSaga = getRoutineSaga(lockContent, createLock);

export const isNotUnlocking = (session: Pick<EditingSession, 'unlocking'>): boolean => !session.unlocking;
export const isNotLocking = (session: Pick<EditingSession, 'locking'>): boolean => !session.locking;

export function sagaFilter<Action extends PayloadMetaAction<string, unknown, ContentId>>(
  predicate: (editingSession: EditingSessionInfo) => boolean,
  saga: (action: Action) => SagaIterator
) {
  return function* (action: Action): SagaIterator {
    const { contentId } = action.meta;
    const session: EditingSessionInfo = yield select(getEditingSession, {
      contentId
    });
    if (predicate(session)) {
      yield call(saga, action);
    }
  };
}

export function* waitFor(actionName: string, contentId: string): SagaIterator {
  while (true) {
    const action = yield take(actionName);
    if (action.payload.contentId === contentId || (action.meta && action.meta.contentId === contentId)) {
      return action;
    }
  }
}

export function* handleLocking(routine: LockRoutine, ids: ContentId): SagaIterator<EditingSessionInfo> {
  const { contentId } = ids;
  yield put(routine.requested(undefined, ids));
  yield race([call(waitFor, routine.FULFILLED, contentId), call(waitFor, routine.REJECTED, contentId)]);
  return yield select(getEditingSession, { contentId });
}

export function* waitForLock(ids: ContentId): SagaIterator<{ waited: boolean; lock: Lock | null }> {
  let waited = false;
  let session: EditingSessionInfo = yield call(handleLocking, lockContent, ids);

  let { hasLock } = session;
  if (hasLock) {
    return { waited, lock: session.lock };
  }

  const { contentId } = ids;
  while (!hasLock) {
    yield call(waitFor, contentUnlocked.toString(), contentId);
    waited = true;

    session = yield call(handleLocking, lockContent, ids);
    hasLock = session.hasLock;
  }

  return { waited, lock: session.lock };
}

export function* waitUntilUnix(epochSec: number): SagaIterator {
  const now = yield call([Date, 'now']);
  const mSecToWait = epochSec * 1000 - now;
  if (mSecToWait > 0) {
    yield delay(mSecToWait);
  }

  return true;
}

export function* getContentSaga(ids: GetContentMeta): SagaIterator {
  yield put(getContent.requested(undefined, ids));
  yield race([call(waitFor, getContent.FULFILLED, ids.contentId), call(waitFor, getContent.REJECTED, ids.contentId)]);
}

export function* editingSessionSaga(ids: GetContentMeta): SagaIterator {
  const [{ waited }] = yield all([call(waitForLock, ids), call(getContentSaga, ids)]);

  if (waited) {
    yield call(getContentSaga, ids);
  }

  yield put(editingSession.fulfilled(ids));
}

export type LockBrokenDialogParams = {
  hadLock: boolean;
  locale: string;
} & LockBrokenPayload;

export function* getDialogConfig({ shapeId, user, hadLock }: LockBrokenDialogParams): SagaIterator {
  const shape: ShapeJSON = yield select(getShapeById, { shapeId });
  const userName = user.fullName || user.email;
  const noun = shape.title;
  const adverb = shape.model?.type === 'single' ? 'the' : 'this';
  const ending = hadLock ? ' while you were editing. Any unsaved changes were lost.' : '.';
  return {
    title: `${noun} has been unlocked`,
    message: `${userName} unlocked ${adverb} ${noun}${ending}`
  };
}

export function* showBrokenLockDialog(payload: LockBrokenDialogParams): SagaIterator {
  const config = yield call(getDialogConfig, payload);
  yield put({
    type: SHOW_ALERT,
    payload: config
  });

  yield take(OK);
  yield put(
    editingSession.requested({
      shapeId: payload.shapeId,
      locale: payload.locale,
      contentId: payload.contentId
    })
  );
}

export function* waitForLockBreak(contentId: string, locale: string): SagaIterator {
  const userId = yield select(getUserId);
  let action: LockBrokenAction;
  let hadLock = false;

  while (true) {
    action = yield call(waitFor, lockBroken.toString(), contentId);
    const showDialog = action.payload.user.id !== userId;
    if (showDialog) {
      const session: EditingSessionInfo = yield select(getEditingSession, {
        contentId
      });
      hadLock = session.hasLock;
    }

    const { user, shapeId } = action.payload;
    yield put(contentUnlocked({ userId: user.id, shapeId, contentId }));

    if (showDialog) {
      break;
    }
  }

  yield spawn(showBrokenLockDialog, { ...action.payload, locale, hadLock });
  return true;
}

export const CLEANUP_DELAY = 500;

export function* cleanupEditingSessionsSaga(): SagaIterator {
  yield delay(CLEANUP_DELAY);
  const inactiveLocks: ContentId[] = yield select(getInactiveLocks);
  for (const lock of inactiveLocks) {
    yield put(unlockContent.requested(undefined, lock));
  }
}

export function* startEditingSessionSaga(action: ActionType<typeof editingSession.requested>): SagaIterator {
  const { shapeId, contentId, locale } = action.payload;
  const projectId: string = yield select(getProjectId);
  const shape: ShapeJSON = yield select(getShapeById, { shapeId });

  const isAllowedToSave = yield select(getPermission, {
    action: 'graphql:execute',
    resource: `ts:graphql:${projectId}:Mutation.${getDefaultMutationName(shape.name, 'shapedb:update')}`
  });

  if (!isAllowedToSave) {
    yield call(getContentSaga, action.payload);
    return;
  }

  const session: EditingSessionInfo = yield select(getEditingSession, {
    contentId
  });
  if (!session.active) {
    yield put(editingSession.pending(action.payload));
    const editingSessionTask = yield fork(editingSessionSaga, action.payload);

    yield race({
      ended: call(waitFor, editingSession.CANCELLED, contentId),
      broken: call(waitForLockBreak, contentId, locale)
    });

    yield cancel(editingSessionTask);
    yield put(cleanupEditingSessions());
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(cleanupEditingSessions, cleanupEditingSessionsSaga),
    takeLatest(breakLock.REQUESTED, breakLockSaga),
    takeEvery(editingSession.REQUESTED, startEditingSessionSaga),
    takeEvery(lockContent.REQUESTED, sagaFilter(isNotLocking, lockContentSaga)),
    takeEvery(unlockContent.REQUESTED, sagaFilter(isNotUnlocking, unlockContentSaga))
  ]);
}
